import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AbstractStateInterface } from '../../interface/abstract';
import { QuestionCreateReq, QuestionInterface } from '../../interface/question';
import { ApiService } from '../../utils/apiService';

type DataState = AbstractStateInterface<QuestionInterface>;

const initialState: DataState = {
  data: [],
  detail: null,
  loading: false,
};

export const getAllQuestionSet = createAsyncThunk('/admin-api/questions-set/list', async (_, thunkAPI) => {
  try {
    const response = await ApiService(`/admin-api/questions-set/list`, 'GET', null);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

export const findQuestionSetById = createAsyncThunk(
  '/admin-api/questions-set',
  async (payload: { qsId: string }, thunkAPI) => {
    const { qsId } = payload;
    try {
      const response = await ApiService(`/admin-api/questions-set/${qsId}`, 'GET', null);
      if (response.statusCode !== 200) {
        return thunkAPI.rejectWithValue({
          code: response.statusCode,
          error: response.error || 'Login failed',
        });
      }
      return {
        code: response.statusCode,
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        code: error.response?.status || 500,
        error: error.message || 'Error',
      });
    }
  },
);

export const createQuestionSet = createAsyncThunk(
  '/admin-api/questions-set/create-questionSet',
  async (payload: QuestionCreateReq, thunkAPI) => {
    try {
      const response = await ApiService(`/admin-api/questions-set/create-questionSet`, 'POST', payload);
      if (response.statusCode !== 200) {
        return thunkAPI.rejectWithValue({
          code: response.statusCode,
          error: response.error || 'Login failed',
        });
      }
      return {
        code: response.statusCode,
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        code: error.response?.status || 500,
        error: error.message || 'Error',
      });
    }
  },
);

export const updateQuestionSet = createAsyncThunk(
  '/admin-api/questions-set/update-questionSet/put',
  async (payload: QuestionInterface, thunkAPI) => {
    const { qsId, ...data } = payload;
    try {
      const response = await ApiService(`/admin-api/questions-set/update-questionSet/${qsId}`, 'PUT', data);
      if (response.statusCode !== 200) {
        return thunkAPI.rejectWithValue({
          code: response.statusCode,
          error: response.error || 'Login failed',
        });
      }
      return {
        code: response.statusCode,
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        code: error.response?.status || 500,
        error: error.message || 'Error',
      });
    }
  },
);

export const deleteQuestionSet = createAsyncThunk('/admin-api/questions-set', async (qsId: string, thunkAPI) => {
  try {
    const response = await ApiService(`/admin-api/questions-set/${qsId}`, 'DELETE', null);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

const questionSlice = createSlice({
  name: 'question',
  initialState,
  reducers: {
    resetQuestionSlice: () => {
      return { ...initialState };
    },
    resetDetail: (state) => {
      state.detail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllQuestionSet.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllQuestionSet.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload.data)) {
          state.data = action.payload.data as QuestionInterface[];
        } else {
          state.data = [];
        }
      })
      .addCase(getAllQuestionSet.rejected, (state) => {
        state.loading = false;
      })
      .addCase(findQuestionSetById.pending, (state) => {
        state.loading = true;
      })
      .addCase(findQuestionSetById.fulfilled, (state, action) => {
        state.detail = action.payload.data as QuestionInterface;
      })
      .addCase(findQuestionSetById.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateQuestionSet.rejected, (state, action) => {
        state.loading = false;
      });
  },
});

export const { resetQuestionSlice, resetDetail } = questionSlice.actions;
export default questionSlice.reducer;
